/** Milestones, dependencies, assignments and workload. */

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'

import { api } from '@/lib/api/client'

export interface Milestone {
  id: string
  projectId: string
  title: string
  dueDate: string | null
  isComplete: boolean
  position: number
  notes: string
  state: 'complete' | 'overdue' | 'due_soon' | 'upcoming' | 'undated'
  /** The work linked to it. Present on the list; null on add/update responses. */
  progress: MilestoneProgress | null
  rowVersion: number
}

export interface MilestoneProgress {
  itemCount: number
  committedPoints: number
  donePoints: number
  percentComplete: number
  /** Every linked item finished. Shown as a prompt — it never ticks the milestone. */
  allFinished: boolean
}

export interface DependencyLink {
  id: string
  projectId: string
  name: string
  status: string
  ragStatus?: string
  percentComplete?: number
  note: string
}

export interface Assignment {
  id: string
  userId: string
  displayName: string
  email: string
  role: 'lead' | 'contributor' | 'stakeholder'
}

export interface Inversion {
  projectId: string
  projectName: string
  dependsOnId: string
  dependsOnName: string
  projectRank: number
  dependsOnRank: number | null
}

export interface WorkloadPerson {
  userId: string
  displayName: string
  email: string
  leading: number
  contributing: number
  atRisk: number
  projects: { id: string; name: string; role: string; ragStatus: string; status: string }[]
}

const milestoneKey = (projectId: string) => ['milestones', projectId] as const
const dependencyKey = (projectId: string) => ['dependencies', projectId] as const
const assignmentKey = (projectId: string) => ['assignments', projectId] as const

// --- milestones ------------------------------------------------------------

export function useMilestones(projectId: string) {
  return useQuery({
    queryKey: milestoneKey(projectId),
    // A work item with no project has no milestones to offer, and '' is not a project.
    enabled: Boolean(projectId),
    queryFn: async () => {
      const { data } = await api.GET('/api/v1/projects/{project_id}/milestones', {
        params: { path: { project_id: projectId } },
      })
      return (data as unknown as { milestones: Milestone[] }).milestones
    },
  })
}

export function useAddMilestone(projectId: string) {
  const client = useQueryClient()
  return useMutation({
    mutationFn: async (body: { title: string; due_date?: string | null; notes?: string }) => {
      const { data } = await api.POST('/api/v1/projects/{project_id}/milestones', {
        params: { path: { project_id: projectId } },
        body: body as never,
      })
      return data as unknown as Milestone
    },
    onSuccess: () => void client.invalidateQueries({ queryKey: milestoneKey(projectId) }),
  })
}

export function useUpdateMilestone(projectId: string) {
  const client = useQueryClient()
  return useMutation({
    mutationFn: async ({
      id,
      ...body
    }: {
      id: string
      title?: string
      due_date?: string | null
      clear_due_date?: boolean
      is_complete?: boolean
      row_version?: number
    }) => {
      const { data } = await api.PATCH('/api/v1/milestones/{milestone_id}', {
        params: { path: { milestone_id: id } },
        body: body as never,
      })
      return data as unknown as Milestone
    },
    onSuccess: () => void client.invalidateQueries({ queryKey: milestoneKey(projectId) }),
  })
}

export function useDeleteMilestone(projectId: string) {
  const client = useQueryClient()
  return useMutation({
    mutationFn: async ({ id, reason }: { id: string; reason: string }) => {
      await api.DELETE('/api/v1/milestones/{milestone_id}', {
        params: { path: { milestone_id: id } },
        body: { reason } as never,
      })
    },
    onSuccess: () => void client.invalidateQueries({ queryKey: milestoneKey(projectId) }),
  })
}

// --- dependencies ----------------------------------------------------------

export function useDependencies(projectId: string) {
  return useQuery({
    queryKey: dependencyKey(projectId),
    queryFn: async () => {
      const { data } = await api.GET('/api/v1/projects/{project_id}/dependencies', {
        params: { path: { project_id: projectId } },
      })
      return data as unknown as { dependsOn: DependencyLink[]; blocks: DependencyLink[] }
    },
  })
}

export function useAddDependency(projectId: string) {
  const client = useQueryClient()
  return useMutation({
    mutationFn: async (body: { depends_on_id: string; note?: string }) => {
      const { data } = await api.POST('/api/v1/projects/{project_id}/dependencies', {
        params: { path: { project_id: projectId } },
        body: body as never,
      })
      return data
    },
    onSuccess: () => {
      void client.invalidateQueries({ queryKey: dependencyKey(projectId) })
      void client.invalidateQueries({ queryKey: ['inversions'] })
    },
  })
}

export function useRemoveDependency(projectId: string) {
  const client = useQueryClient()
  return useMutation({
    mutationFn: async ({ id, reason }: { id: string; reason: string }) => {
      await api.DELETE('/api/v1/dependencies/{dependency_id}', {
        params: { path: { dependency_id: id } },
        body: { reason } as never,
      })
    },
    onSuccess: () => {
      void client.invalidateQueries({ queryKey: dependencyKey(projectId) })
      void client.invalidateQueries({ queryKey: ['inversions'] })
    },
  })
}

export function useInversions() {
  return useQuery({
    queryKey: ['inversions'],
    queryFn: async () => {
      const { data } = await api.GET('/api/v1/dependencies/inversions', {})
      return (data as unknown as { inversions: Inversion[] }).inversions
    },
  })
}

// --- assignments and workload ---------------------------------------------

export function useAssignments(projectId: string) {
  return useQuery({
    queryKey: assignmentKey(projectId),
    queryFn: async () => {
      const { data } = await api.GET('/api/v1/projects/{project_id}/assignments', {
        params: { path: { project_id: projectId } },
      })
      return (data as unknown as { assignments: Assignment[] }).assignments
    },
  })
}

export function useAssign(projectId: string) {
  const client = useQueryClient()
  return useMutation({
    mutationFn: async (body: { user_id: string; role: Assignment['role'] }) => {
      const { data } = await api.POST('/api/v1/projects/{project_id}/assignments', {
        params: { path: { project_id: projectId } },
        body: body as never,
      })
      return data
    },
    onSuccess: () => {
      void client.invalidateQueries({ queryKey: assignmentKey(projectId) })
      void client.invalidateQueries({ queryKey: ['workload'] })
    },
  })
}

export function useUnassign(projectId: string) {
  const client = useQueryClient()
  return useMutation({
    mutationFn: async ({ id, reason }: { id: string; reason: string }) => {
      await api.DELETE('/api/v1/assignments/{assignment_id}', {
        params: { path: { assignment_id: id } },
        body: { reason } as never,
      })
    },
    onSuccess: () => {
      void client.invalidateQueries({ queryKey: assignmentKey(projectId) })
      void client.invalidateQueries({ queryKey: ['workload'] })
    },
  })
}

export function useWorkload() {
  return useQuery({
    queryKey: ['workload'],
    queryFn: async () => {
      const { data } = await api.GET('/api/v1/workload', {})
      return data as unknown as { people: WorkloadPerson[]; totalAssigned: number }
    },
  })
}
